import React, { useState, useEffect } from 'react';
import CameraCapture from '../components/CameraCapture';
import { CheckCircle, AlertCircle, Loader } from 'lucide-react';

// Public, no-login driver onboarding form. A new starter opens this via a tokenised
// link (/onboard?token=…) on their phone, completes their details + document uploads,
// and it's STAGED on their applicant record for the operator to review and activate.

const API = '/.netlify/functions/applicants-sb';   // onboarding now on Supabase

// CameraCapture returns a data URL (data:image/jpeg;base64,…). Split into the shape
// the backend's storeApplicantImage_ expects: { label, photoBase64, contentType }.
function dataUrlToDoc(label, dataUrl) {
  if (!dataUrl) return null;
  const comma = dataUrl.indexOf(',');
  if (comma < 0) return null;
  const contentType = (dataUrl.slice(0, comma).match(/:(.*?);/) || [])[1] || 'image/jpeg';
  return { label, photoBase64: dataUrl.slice(comma + 1), contentType };
}

const wrap = { maxWidth: 560, margin: '0 auto', padding: '20px 16px 64px' };
const card = { background: 'var(--hp-surface, #fff)', border: '1px solid var(--hp-border, #e5e7eb)', borderRadius: 12, padding: 16, marginBottom: 14 };
const label = { display: 'block', fontSize: '.8rem', fontWeight: 600, color: 'var(--hp-text, #111827)', marginBottom: 5 };
const input = { width: '100%', padding: '11px 12px', borderRadius: 8, border: '1px solid var(--hp-border, #d1d5db)', fontSize: '16px', boxSizing: 'border-box', background: 'var(--hp-bg, #fff)', color: 'var(--hp-text, #111)' };
const sectionTitle = { fontSize: '.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--hp-gold, #b8923a)', marginBottom: 12 };
const fieldGap = { marginBottom: 12 };

function Field({ lbl, children, hint }) {
  return (
    <div style={fieldGap}>
      <label style={label}>{lbl}</label>
      {children}
      {hint && <div style={{ fontSize: '.7rem', color: 'var(--hp-dim, #9ca3af)', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}
function Radio({ name, value, current, onChange, children }) {
  const active = current === value;
  return (
    <button type="button" onClick={() => onChange(value)}
      style={{ flex: 1, padding: '11px 8px', borderRadius: 8, fontSize: '.85rem', fontWeight: 600, cursor: 'pointer',
        border: `1.5px solid ${active ? 'var(--hp-gold, #b8923a)' : 'var(--hp-border, #d1d5db)'}`,
        background: active ? 'var(--hp-gold, #b8923a)' : 'transparent', color: active ? '#fff' : 'var(--hp-text, #374151)' }}>
      {children}
    </button>
  );
}

export default function OnboardingPage() {
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [prefill, setPrefill] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false); // false | true | 'already'

  const [f, setF] = useState({
    preferredName: '',
    mobile: '', email: '',
    sameAddress: true, currentAddress: '',
    emergencyName: '', emergencyPhone: '', emergencyRelationship: '',
    citizen: '', country: '', visaType: '', grantNumber: '', passportNumber: '', dateOfBirth: '', vevoConsent: false,
    acn: '', gstRegistered: '',
    workersCompPolicy: '', workersCompExpiry: '',
    accountName: '', bsb: '', accNumber: '',
  });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  // Documents the office already holds for this person (e.g. a licence scanned at interview).
  const onFile = (lbl) => (prefill?.documentsOnFile || []).includes(lbl);
  const [passportDoc, setPassportDoc] = useState(null);
  const [passportBackDoc, setPassportBackDoc] = useState(null); // rear — only for an AU Citizenship certificate (number is on the back)
  const [workersCompDoc, setWorkersCompDoc] = useState(null);

  useEffect(() => {
    if (!token) { setError('This onboarding link is missing its token. Please use the exact link you were sent.'); setLoading(false); return; }
    fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'getOnboarding', token }) })
      .then(r => r.json())
      .then(d => {
        if (!d || !d.success) { setError((d && d.error) || 'This onboarding link is not valid.'); return; }
        setPrefill(d);
        setF(p => ({ ...p, mobile: d.mobile || '', email: d.email || '', ...(d.onboarding || {}) }));
        if (d.submittedAt) setDone('already');
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  async function submit() {
    setError('');
    if (!f.citizen) return setError('Please tell us whether you’re an Australian citizen / resident.');
    if (f.citizen === 'no' && (!f.country || !f.visaType || !f.grantNumber)) return setError('Please complete your country, visa type and grant number.');
    if (f.citizen === 'no' && !f.passportNumber) return setError('Please enter your passport number.');
    if (f.citizen === 'no' && !f.dateOfBirth && !prefill?.dateOfBirth) return setError('Please enter your date of birth.');
    if (f.citizen === 'no' && !passportDoc && !onFile('Passport')) return setError('Please upload a photo of your passport.');
    if (f.citizen === 'no' && !f.vevoConsent) return setError('Please tick the box to agree to the work-rights check.');
    if (f.sameAddress === false && !f.currentAddress) return setError('Please enter your current residential address (or choose “Yes” if it matches your licence).');
    if (!f.emergencyName || !f.emergencyPhone) return setError('Please add an emergency contact name and phone.');
    if (!f.accountName || !f.bsb || !f.accNumber) return setError('Please enter your bank account name, BSB and account number.');
    if (!f.gstRegistered) return setError('Please tell us your GST registration status.');

    setSubmitting(true);
    const documents = [];
    const pDoc = dataUrlToDoc(f.citizen === 'yes' ? 'Passport / Birth Certificate' : 'Passport', passportDoc);
    if (pDoc) documents.push(pDoc);
    if (f.citizen === 'yes') {
      // Rear of an Australian Citizenship certificate — the certificate/stock number is on the back.
      const bDoc = dataUrlToDoc('Australian Citizenship — Back', passportBackDoc);
      if (bDoc) documents.push(bDoc);
    }
    const wDoc = dataUrlToDoc('Workers Compensation', workersCompDoc);
    if (wDoc) documents.push(wDoc);
    try {
      const res = await fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'submitOnboarding', token, onboarding: f, documents }) });
      const d = await res.json();
      if (d && d.success) setDone(true);
      else setError((d && d.error) || 'Could not submit — please check your connection and try again.');
    } catch (e) { setError(e.message); }
    finally { setSubmitting(false); }
  }

  if (loading) return <div style={{ ...wrap, textAlign: 'center', paddingTop: 80, color: 'var(--hp-dim,#9ca3af)' }}><Loader size={28} style={{ animation: 'spin 1s linear infinite' }} /><div style={{ marginTop: 10 }}>Loading…</div></div>;

  if (error && !prefill) return (
    <div style={{ ...wrap, paddingTop: 60 }}>
      <div style={{ ...card, textAlign: 'center' }}>
        <AlertCircle size={32} color="#dc2626" />
        <div style={{ fontWeight: 700, marginTop: 10 }}>Link not valid</div>
        <div style={{ color: 'var(--hp-dim,#6b7280)', fontSize: '.85rem', marginTop: 6 }}>{error}</div>
      </div>
    </div>
  );

  if (done) return (
    <div style={{ ...wrap, paddingTop: 60 }}>
      <div style={{ ...card, textAlign: 'center' }}>
        <CheckCircle size={40} color="#16a34a" />
        <div style={{ fontWeight: 700, fontSize: '1.1rem', marginTop: 12 }}>{done === 'already' ? 'Already submitted' : 'Thank you!'}</div>
        <div style={{ color: 'var(--hp-dim,#6b7280)', fontSize: '.9rem', marginTop: 8 }}>
          {done === 'already'
            ? 'Your details have already been received. If you need to change something, let your manager know.'
            : 'Your details have been sent to Hollypaul. We’ll be in touch — welcome aboard!'}
        </div>
      </div>
    </div>
  );

  return (
    <div style={wrap}>
      <div style={{ textAlign: 'center', marginBottom: 18 }}>
        <div style={{ fontFamily: 'var(--font-display, serif)', fontWeight: 800, fontSize: '1.4rem', letterSpacing: '.04em', color: 'var(--hp-gold, #b8923a)' }}>HOLLYPAUL</div>
        <div style={{ fontSize: '.95rem', fontWeight: 700, marginTop: 8 }}>Welcome{prefill?.fullName ? `, ${prefill.fullName.split(' ')[0]}` : ''}! 👋</div>
        <div style={{ fontSize: '.82rem', color: 'var(--hp-dim,#6b7280)', marginTop: 4 }}>Complete your details to get set up as a driver. Takes a few minutes — have your bank details and visa/passport handy.</div>
      </div>

      {/* Contact */}
      <div style={card}>
        <div style={sectionTitle}>Your contact details</div>
        {prefill?.fullName && <Field lbl="Full name"><input style={{ ...input, background: '#f3f4f6', color: '#6b7280' }} value={prefill.fullName} disabled /></Field>}
        <Field lbl="Preferred name" hint="What you’d like to be called — e.g. Rob"><input style={input} value={f.preferredName} onChange={e => set('preferredName', e.target.value)} placeholder="Rob" /></Field>
        <Field lbl="Mobile"><input style={input} type="tel" value={f.mobile} onChange={e => set('mobile', e.target.value)} /></Field>
        <Field lbl="Email"><input style={input} type="email" value={f.email} onChange={e => set('email', e.target.value)} /></Field>
      </div>

      {/* Address */}
      <div style={card}>
        <div style={sectionTitle}>Current address</div>
        <Field lbl="Is your current address the same as on your driver’s licence?">
          <div style={{ display: 'flex', gap: 8 }}>
            <Radio value="yes" current={f.sameAddress === false ? 'no' : 'yes'} onChange={() => set('sameAddress', true)}>Yes</Radio>
            <Radio value="no" current={f.sameAddress === false ? 'no' : 'yes'} onChange={() => set('sameAddress', false)}>No</Radio>
          </div>
        </Field>
        {f.sameAddress === false && <Field lbl="Current residential address" hint="Your address now, if it’s different to your licence."><input style={input} value={f.currentAddress} onChange={e => set('currentAddress', e.target.value)} placeholder="Unit / street / suburb / state / postcode" /></Field>}
      </div>

      {/* Emergency contact */}
      <div style={card}>
        <div style={sectionTitle}>Emergency contact</div>
        <Field lbl="Name"><input style={input} value={f.emergencyName} onChange={e => set('emergencyName', e.target.value)} /></Field>
        <Field lbl="Phone"><input style={input} type="tel" value={f.emergencyPhone} onChange={e => set('emergencyPhone', e.target.value)} /></Field>
        <Field lbl="Relationship"><input style={input} value={f.emergencyRelationship} onChange={e => set('emergencyRelationship', e.target.value)} placeholder="e.g. partner, parent, friend" /></Field>
      </div>

      {/* Residency */}
      <div style={card}>
        <div style={sectionTitle}>Residency</div>
        <Field lbl="Are you an Australian citizen or resident?">
          <div style={{ display: 'flex', gap: 8 }}>
            <Radio value="yes" current={f.citizen} onChange={v => set('citizen', v)}>Yes</Radio>
            <Radio value="no" current={f.citizen} onChange={v => set('citizen', v)}>No</Radio>
          </div>
        </Field>
        {f.citizen === 'yes' && (
          <>
            <Field lbl="Upload your citizenship certificate, passport or birth certificate" hint="Tap to take a photo or upload a file. This is the front of the document.">
              <CameraCapture nativeFirst guide="document" maxEdge={1600} quality={0.85} value={passportDoc} onCapture={setPassportDoc} onClear={() => setPassportDoc(null)} />
            </Field>
            <Field lbl="Back of the document" hint="Uploading an Australian Citizenship certificate? Add a photo of the back too — the certificate number is on the rear. Leave blank for a passport or birth certificate.">
              <CameraCapture nativeFirst guide="document" maxEdge={1600} quality={0.85} value={passportBackDoc} onCapture={setPassportBackDoc} onClear={() => setPassportBackDoc(null)} />
            </Field>
          </>
        )}
        {f.citizen === 'no' && (
          <>
            <Field lbl="Country of citizenship"><input style={input} value={f.country} onChange={e => set('country', e.target.value)} /></Field>
            <Field lbl="Visa type"><input style={input} value={f.visaType} onChange={e => set('visaType', e.target.value)} placeholder="e.g. Student 500, Working Holiday 417" /></Field>
            <Field lbl="Visa grant number"><input style={input} value={f.grantNumber} onChange={e => set('grantNumber', e.target.value)} /></Field>
            <Field lbl="Passport number" hint="Exactly as printed on your passport."><input style={input} value={f.passportNumber} onChange={e => set('passportNumber', e.target.value.toUpperCase())} autoCapitalize="characters" /></Field>
            {!prefill?.dateOfBirth && (
              <Field lbl="Date of birth"><input type="date" style={input} value={f.dateOfBirth} onChange={e => set('dateOfBirth', e.target.value)} /></Field>
            )}
            <Field lbl="Upload your passport" hint={onFile('Passport') ? 'We already have your passport photo. Only add a new one if it has changed.' : 'The photo page. Tap to take a photo or upload a file.'}>
              <CameraCapture nativeFirst guide="document" maxEdge={1600} quality={0.85} value={passportDoc} onCapture={setPassportDoc} onClear={() => setPassportDoc(null)} />
            </Field>
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: '.84rem', lineHeight: 1.45, marginTop: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!f.vevoConsent} onChange={e => set('vevoConsent', e.target.checked)} style={{ marginTop: 3, width: 18, height: 18, flexShrink: 0 }} />
              <span>{prefill?.vevoConsentText || 'I consent to Hollypaul checking my visa and work rights with the Department of Home Affairs (VEVO), using my passport details, through its provider vSure.'}</span>
            </label>
          </>
        )}
      </div>

      {/* Business / GST */}
      <div style={card}>
        <div style={sectionTitle}>Business details</div>
        <Field lbl="ACN — Australian Company Number" hint="Optional — leave blank if you don’t have one."><input style={input} value={f.acn} onChange={e => set('acn', e.target.value)} /></Field>
        <Field lbl="Are you registered for GST?">
          <div style={{ display: 'flex', gap: 8 }}>
            <Radio value="yes" current={f.gstRegistered} onChange={v => set('gstRegistered', v)}>Yes</Radio>
            <Radio value="no" current={f.gstRegistered} onChange={v => set('gstRegistered', v)}>No</Radio>
            <Radio value="unsure" current={f.gstRegistered} onChange={v => set('gstRegistered', v)}>Unsure</Radio>
          </div>
        </Field>
      </div>

      {/* Workers comp */}
      <div style={card}>
        <div style={sectionTitle}>Workers compensation</div>
        <Field lbl="Policy number"><input style={input} value={f.workersCompPolicy} onChange={e => set('workersCompPolicy', e.target.value)} /></Field>
        <Field lbl="Expiry date"><input style={input} type="date" value={f.workersCompExpiry} onChange={e => set('workersCompExpiry', e.target.value)} /></Field>
        <Field lbl="Upload policy details" hint="A photo or PDF screenshot of your certificate of currency.">
          <CameraCapture nativeFirst guide="document" maxEdge={1600} quality={0.85} value={workersCompDoc} onCapture={setWorkersCompDoc} onClear={() => setWorkersCompDoc(null)} />
        </Field>
      </div>

      {/* Bank */}
      <div style={card}>
        <div style={sectionTitle}>Bank account (company)</div>
        <Field lbl="Account name"><input style={input} value={f.accountName} onChange={e => set('accountName', e.target.value)} /></Field>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ width: 130 }}><Field lbl="BSB"><input style={input} inputMode="numeric" value={f.bsb} onChange={e => set('bsb', e.target.value)} placeholder="000-000" /></Field></div>
          <div style={{ flex: 1 }}><Field lbl="Account number"><input style={input} inputMode="numeric" value={f.accNumber} onChange={e => set('accNumber', e.target.value)} /></Field></div>
        </div>
      </div>

      <div style={{ ...card, background: 'var(--hp-bg, #f9fafb)' }}>
        <div style={sectionTitle}>Your privacy &amp; security</div>
        <div style={{ fontSize: '.78rem', color: 'var(--hp-dim, #6b7280)', lineHeight: 1.5 }}>
          Everything you send is <strong>encrypted in transit and stored encrypted</strong>, in private storage — never on
          a public or shareable link. Only Hollypaul’s <strong>authorised admins can open your documents, and only after
          signing in</strong>. We collect this solely to set you up as a driver and meet our obligations under the
          Australian Privacy Act, and you can ask us to access or delete it at any time.
        </div>
      </div>

      {error && <div style={{ ...card, background: '#fef2f2', border: '1px solid #fca5a5', color: '#b91c1c', fontSize: '.85rem', display: 'flex', gap: 8, alignItems: 'flex-start' }}><AlertCircle size={18} style={{ flexShrink: 0, marginTop: 1 }} />{error}</div>}

      <button type="button" onClick={submit} disabled={submitting}
        style={{ width: '100%', padding: '15px', borderRadius: 10, border: 'none', background: 'var(--hp-gold, #b8923a)', color: '#fff', fontSize: '1rem', fontWeight: 700, cursor: 'pointer', opacity: submitting ? 0.6 : 1 }}>
        {submitting ? 'Submitting…' : 'Submit my details'}
      </button>
      <div style={{ textAlign: 'center', fontSize: '.7rem', color: 'var(--hp-dim,#9ca3af)', marginTop: 12 }}>Your information is sent securely to Hollypaul Recruitment Pty Ltd.</div>
    </div>
  );
}
